import {GriddedSlice, basinAverageSeries} from './aggregate';
import {WeightGrid} from './basinWeights';
import {DynamicalUnavailableError} from './errors';
import type {WorkerRequest, WorkerResponse} from './worker';

/**
 * Runs the NaN-skipping weighted reduction off the main thread: a GEFS init is
 * ~1.5M floats across 31 members, enough to jank the UI if reduced inline. Falls
 * back to the inline implementation where module workers are unavailable
 * (vitest/jsdom, or a worker that fails to boot).
 */

let worker: Worker | null | undefined;
let nextRequestId = 0;
const pending = new Map<number, {resolve: (v: Float64Array) => void; reject: (e: Error) => void}>();

function getWorker(): Worker | null {
  if (worker !== undefined) {
    return worker;
  }
  if (typeof Worker === 'undefined') {
    worker = null;
    return worker;
  }
  try {
    worker = new Worker(new URL('./worker.ts', import.meta.url), {type: 'module'});
    worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
      const {id, result, error} = event.data;
      const entry = pending.get(id);
      if (!entry) {
        return;
      }
      pending.delete(id);
      if (result) {
        entry.resolve(result);
      } else {
        entry.reject(new Error(error ?? 'dynamical worker failed'));
      }
    };
    worker.onerror = (event) => {
      // Fail all in-flight requests; callers fall back to the API path. Their
      // slice buffers are already transferred, so they can't be re-reduced
      // inline. The usual cause is the worker module itself failing to load in a
      // session that predates the latest deploy, which makes the session unable
      // to serve direct reads rather than wrong — later calls take the inline
      // path, since getWorker() now returns null.
      const reason = new DynamicalUnavailableError(
        `dynamical worker unavailable: ${event.message || 'failed to start'}`
      );
      for (const entry of pending.values()) {
        entry.reject(reason);
      }
      pending.clear();
      worker?.terminate();
      worker = null;
    };
  } catch {
    worker = null;
  }
  return worker;
}

export async function basinAverageSeriesOffThread(
  slice: GriddedSlice,
  weightGrid: WeightGrid
): Promise<Float64Array> {
  const activeWorker = getWorker();
  if (!activeWorker) {
    return basinAverageSeries(slice, weightGrid);
  }
  const id = nextRequestId;
  nextRequestId += 1;
  return new Promise<Float64Array>((resolve, reject) => {
    pending.set(id, {resolve, reject});
    try {
      // The slice buffer is transferred (zero-copy) — it must not be reused after
      // this call. Weights are structured-cloned since they're cached and reused.
      activeWorker.postMessage({id, slice, weightGrid} satisfies WorkerRequest, [
        slice.data.buffer,
      ]);
    } catch {
      // A serialization failure happens before the buffer is transferred, so the
      // slice is still intact for the inline fallback.
      pending.delete(id);
      resolve(basinAverageSeries(slice, weightGrid));
    }
  });
}
